import { useState } from "react";
import type { RouterOutputs } from "@/lib/trpc";
import { CheckCircle2, History, ChevronRight, Undo2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/** Rows shown by default — ~2 months of weeks; the rest behind "Show all". */
const RECENT_COUNT = 8;

/**
 * Receipt history — the caller's chronological archive of approved WEEKLY
 * receipts (`profile.approvedWeeklies`, server-scoped to the caller, newest
 * first). Read-only record: there is no editing or re-approval here and
 * nothing is recomputed client-side — but each row is TAPPABLE and jumps the
 * Drafts tab to that week, where the full weekly + dailies + the normal
 * (gated) actions live. Retracted weeklies stay listed (they are still the
 * owner's history) but are labeled honestly instead of showing a green
 * "approved".
 */
type Weeklies = RouterOutputs["profile"]["approvedWeeklies"];
type Weekly = Weeklies[number];

function weekRangeLabel(periodKey: string): string {
  // periodKey is the week-start YYYY-MM-DD; display the 7-day span (UTC to
  // match the stored key — NOT a client timezone decision).
  const start = new Date(periodKey + "T00:00:00.000Z");
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const f = (d: Date) =>
    d.toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });
  return `${f(start)} – ${f(end)}`;
}

function shortDate(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString("en", { month: "short", day: "numeric" });
}

function Row({ row, onOpen }: { row: Weekly; onOpen?: () => void }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const headline = (row.content as any)?.headline as string | undefined;
  const retracted = (row as any).retractedAt != null;
  // Post-approval edit (status fell to "edited") → the team still sees the
  // older snapshot; say so instead of echoing the raw enum.
  const edited = row.status === "edited";
  const tag = retracted
    ? isAr
      ? "مسحوب"
      : "Retracted"
    : edited
      ? isAr
        ? "تغييرات غير مُشارَكة"
        : "Changes not shared"
      : isAr
        ? `معتمد · ${shortDate((row as any).approvedAt)}`
        : `Approved · ${shortDate((row as any).approvedAt)}`;

  const inner = (
    <>
      <div className="h-8 w-8 rounded-lg bg-sand/50 grid place-items-center shrink-0 mt-0.5">
        {retracted ? (
          <Undo2 className="h-4 w-4 text-muted-foreground" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        )}
      </div>
      <div className="min-w-0 flex-1 text-start">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{weekRangeLabel(row.periodKey)}</span>
          <span
            className={
              retracted || edited
                ? "text-[11px] text-amber-600"
                : "text-[11px] text-muted-foreground"
            }
          >
            {tag}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
          {headline || (isAr ? "ملخّص أسبوعي معتمد" : "Approved weekly receipt")}
        </p>
      </div>
      {onOpen ? (
        <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0 mt-1.5" aria-hidden />
      ) : null}
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-start gap-3 py-3 text-start transition-colors hover:bg-muted/40"
        data-testid="history-row"
        aria-label={
          isAr ? `فتح أسبوع ${weekRangeLabel(row.periodKey)}` : `Open week ${weekRangeLabel(row.periodKey)}`
        }
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex items-start gap-3 py-3" data-testid="history-row">
      {inner}
    </div>
  );
}

export function ReceiptHistoryList({
  weeklies,
  onOpenWeek,
}: {
  weeklies: Weeklies;
  /** Jump the Drafts tab to this week (periodKey = week-start YYYY-MM-DD). */
  onOpenWeek?: (periodKey: string) => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [showAll, setShowAll] = useState(false);
  if (!weeklies.length) {
    return (
      <section
        className="rounded-2xl border border-border bg-card/60 p-4"
        data-testid="receipt-history"
      >
        <div className="text-xs font-semibold tracking-wide text-muted-foreground mb-1">
          {isAr ? "سجلّ الملخّصات" : "RECEIPT HISTORY"}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <History className="h-4 w-4" />
          {isAr ? "لا ملخّصات أسبوعية معتمدة بعد." : "No approved weekly receipts yet."}
        </div>
      </section>
    );
  }
  return (
    <section
      className="rounded-2xl border border-border bg-card/60 p-4"
      data-testid="receipt-history"
    >
      <div className="text-xs font-semibold tracking-wide text-muted-foreground mb-1">
        {isAr ? "سجلّ الملخّصات" : "RECEIPT HISTORY"}
      </div>
      <div className="divide-y divide-border">
        {(showAll ? weeklies : weeklies.slice(0, RECENT_COUNT)).map(w => (
          <Row
            key={w.id}
            row={w}
            onOpen={onOpenWeek ? () => onOpenWeek(w.periodKey) : undefined}
          />
        ))}
      </div>
      {weeklies.length > RECENT_COUNT ? (
        <button
          type="button"
          onClick={() => setShowAll(a => !a)}
          className="mt-2 w-full rounded-full border border-border py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
          data-testid="history-toggle"
        >
          {showAll
            ? isAr
              ? "عرض الأحدث"
              : "Show recent"
            : isAr
              ? `عرض الكل (${weeklies.length})`
              : `Show all (${weeklies.length})`}
        </button>
      ) : null}
    </section>
  );
}
